"use client";

import { Link } from "react-router-dom";
import { Eye, MapPin, CalendarDays, Building2, Clock, TrendingUp } from "lucide-react";
import { getApplicationStatus } from "../../utils/applicationStatus";
import useMyApplications from "../../hooks/useMyApplications";

export default function Applications() {
  const { applications, loading, error } = useMyApplications();

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-sm text-slate-500">Loading applications...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 xs:space-y-5 sm:space-y-6">
      <div className="min-w-0">
        <h1 className="text-xl font-bold tracking-tight text-slate-900 xs:text-2xl">My Applications</h1>
        <p className="mt-1 text-xs text-slate-500 xs:text-sm">Track your job applications and hiring progress.</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {applications.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
          <Building2 className="mx-auto text-slate-400" size={40} />
          <h3 className="mt-4 text-lg font-semibold text-slate-800">No Applications Found</h3>
          <p className="mt-2 text-sm text-slate-500">Start applying for jobs to track your hiring progress.</p>
        </div>
      ) : (
        <div className="space-y-3 xs:space-y-4">
          {applications.map((job:any) => {
            const statusText = getApplicationStatus(job.status) || "Unknown";

            return (
              <div
                key={job.applicationId}
                className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition hover:border-slate-300 hover:shadow-md xs:p-4 sm:rounded-2xl sm:p-5"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <h2 className="truncate text-base font-bold text-slate-900 xs:text-lg">
                      {job.job?.title || "Job Application"}
                    </h2>

                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500 xs:text-sm">
                      <span className="flex items-center gap-1.5 font-medium text-slate-700">
                        <Building2 size={15} className="text-slate-400" />
                        {job.job?.company || "Dharohar AI"}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MapPin size={15} className="text-slate-400" />
                        {job.job?.location || "-"}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <CalendarDays size={15} className="text-slate-400" />
                        {job.appliedAt ? new Date(job.appliedAt).toLocaleDateString() : "-"}
                      </span>
                    </div>

                    <div className="mt-4 flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
                      <Clock size={17} className="text-blue-600" />
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Current Status</p>
                        <p className="text-sm font-medium text-slate-700">{statusText}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-col gap-3 border-t border-slate-100 pt-3 lg:items-end lg:border-t-0 lg:pt-0">
                    <div className="flex items-center justify-between gap-4 lg:flex-col lg:items-end">
                      <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                        {statusText}
                      </span>
                      <div className="flex items-center gap-2">
                        <TrendingUp size={16} className="text-emerald-600" />
                        <div>
                          <p className="text-xs text-slate-500">ATS Score</p>
                          <p className="text-sm font-bold text-emerald-600">N/A</p>
                        </div>
                      </div>
                    </div>

                    <Link
                      to={`/candidate/application-details/${job.applicationId}`}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 sm:w-auto"
                    >
                      <Eye size={16} />
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}